import { useState, useEffect, useCallback } from 'react';
import {
  IMAGE_STYLES,
  IMAGE_STYLE_CATEGORIES,
  type ImageStyleCategory,
  type ImageStyleConfig,
} from '@/config/imageStyles';

interface StylePickerModalProps {
  isOpen: boolean;
  selectedStyleIds: string[];
  maxSelections: number;
  onSelect: (styleIds: string[]) => void;
  onClose: () => void;
}

export default function StylePickerModal({
  isOpen,
  selectedStyleIds,
  maxSelections,
  onSelect,
  onClose,
}: StylePickerModalProps) {
  const [activeCategory, setActiveCategory] = useState<ImageStyleCategory>('all');
  const [hovered, setHovered] = useState<string | null>(null);
  const [pendingIds, setPendingIds] = useState<string[]>(selectedStyleIds);
  const [brokenPreviewIds, setBrokenPreviewIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      setPendingIds(selectedStyleIds);
      setActiveCategory('all');
      setBrokenPreviewIds({});
    }
  }, [isOpen, selectedStyleIds]);

  const renderPreview = (style: ImageStyleConfig, className: string) => {
    if (brokenPreviewIds[style.id]) {
      return (
        <div className={`${className} bg-gradient-to-br from-orange-100 via-amber-50 to-stone-200 flex items-center justify-center`}>
          <div className="text-center px-3">
            <i className="ri-sparkling-line text-stone-500 text-2xl" />
            <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-stone-600">{style.label}</p>
          </div>
        </div>
      );
    }

    return (
      <img
        src={style.previewImage}
        alt={style.label}
        className={className}
        loading="lazy"
        onError={() => setBrokenPreviewIds((prev) => ({ ...prev, [style.id]: true }))}
      />
    );
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const filteredStyles: ImageStyleConfig[] =
    activeCategory === 'all'
      ? IMAGE_STYLES
      : IMAGE_STYLES.filter((s) => s.category === activeCategory);

  const handleToggle = (styleId: string) => {
    if (maxSelections === 1) {
      setPendingIds([styleId]);
      return;
    }
    // Count how many times this style is already selected
    const count = pendingIds.filter((id) => id === styleId).length;
    if (count > 0 && pendingIds.length >= maxSelections) {
      // All slots full — remove the last occurrence of this style
      const lastIdx = [...pendingIds].map((id, i) => ({ id, i })).filter(({ id }) => id === styleId).pop()?.i ?? -1;
      if (lastIdx > -1) {
        setPendingIds((prev) => prev.filter((_, i) => i !== lastIdx));
      }
    } else if (pendingIds.length < maxSelections) {
      // Slot available — add another instance
      setPendingIds((prev) => [...prev, styleId]);
    }
  };

  const handleApply = () => {
    onSelect(pendingIds);
    onClose();
  };

  if (!isOpen) return null;

  const isMulti = maxSelections > 1;
  const isFull = pendingIds.length >= maxSelections;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-stone-900">
              {isMulti ? `Pick up to ${maxSelections} styles — one per image` : 'Pick a style for your image'}
            </h2>
            <p className="text-xs text-stone-400 mt-0.5">
              {isMulti ? (
                <span>
                  <span className={`font-semibold ${pendingIds.length === maxSelections ? 'text-orange-500' : 'text-stone-500'}`}>
                    {pendingIds.length} of {maxSelections} selected
                  </span>
                  {' · '}{IMAGE_STYLES.length} styles available · unselected slots use AI auto-mix
                </span>
              ) : `${IMAGE_STYLES.length} styles available`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isMulti && pendingIds.length > 0 && (
              <button
                onClick={() => setPendingIds([])}
                className="text-xs text-stone-400 hover:text-stone-700 px-3 py-1.5 rounded-lg border border-stone-200 hover:border-stone-300 transition-all cursor-pointer whitespace-nowrap"
              >
                Clear all
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Slot preview strip (multi only) */}
        {isMulti && (
          <div className="px-6 pt-4 pb-1 shrink-0">
            <div className="flex gap-1.5">
              {Array.from({ length: maxSelections }).map((_, i) => {
                const styleId = pendingIds[i];
                const style = styleId ? IMAGE_STYLES.find((s) => s.id === styleId) : null;
                return (
                  <div
                    key={i}
                    className={`relative flex-1 h-10 rounded-lg overflow-visible border-2 transition-all ${
                      style ? 'border-orange-400' : 'border-dashed border-stone-200'
                    }`}
                  >
                    {style ? (
                      <>
                        {renderPreview(style, 'w-full h-full object-cover object-top rounded-md')}
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-md">
                          <span className="text-white text-xs font-extrabold">{i + 1}</span>
                        </div>
                        <button
                          onClick={() => setPendingIds((prev) => prev.filter((_, idx) => idx !== i))}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center cursor-pointer z-10 border-2 border-white"
                        >
                          <i className="ri-close-line text-white" style={{ fontSize: '9px' }} />
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-stone-300 text-xs font-bold">{i + 1}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {isFull && (
              <p className="text-xs text-orange-500 font-medium mt-2 flex items-center gap-1.5">
                <i className="ri-check-double-line" />
                All {maxSelections} slots filled — tap a style to swap it out
              </p>
            )}
          </div>
        )}

        {/* Category Filter */}
        <div className="px-6 pt-4 pb-3 flex gap-2 flex-wrap shrink-0">
          {IMAGE_STYLE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-stone-900 border-stone-900 text-white'
                  : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-800'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Style Grid */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <div className="grid grid-cols-4 gap-3">
            {filteredStyles.map((style) => {
              const selectedCount = pendingIds.filter((id) => id === style.id).length;
              const isSelected = selectedCount > 0;
              const isHovered = hovered === style.id;
              // In single-select mode, nothing is ever disabled — clicking always switches
              // In multi-select mode, disabled only if slots are full AND style isn't already selected
              const isDisabled = isMulti && !isSelected && isFull;

              return (
                <button
                  key={style.id}
                  onClick={() => !isDisabled && handleToggle(style.id)}
                  onMouseEnter={() => setHovered(style.id)}
                  onMouseLeave={() => setHovered(null)}
                  className={`relative group rounded-xl overflow-hidden aspect-square transition-all duration-200 ${
                    isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    isSelected
                      ? 'ring-2 ring-orange-500 ring-offset-2'
                      : !isDisabled
                      ? 'hover:ring-2 hover:ring-stone-300 hover:ring-offset-1'
                      : ''
                  }`}
                >
                  {renderPreview(style, 'w-full h-full object-cover object-top')}

                  <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                  <span className="absolute bottom-2 left-2.5 text-white text-xs font-bold leading-tight">
                    {style.label}
                  </span>

                  {/* Selection badge */}
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center shadow-md">
                      {isMulti ? (
                        <span className="text-white text-xs font-extrabold leading-none">
                          {selectedCount > 1 ? `×${selectedCount}` : pendingIds.indexOf(style.id) + 1}
                        </span>
                      ) : (
                        <i className="ri-check-line text-white text-xs" />
                      )}
                    </div>
                  )}

                  {/* "Add again" hint when slots remain and style already selected */}
                  {isSelected && !isFull && isHovered && isMulti && (
                    <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                      <span className="text-white text-xs font-bold text-center px-2 drop-shadow">
                        +Add again
                      </span>
                    </div>
                  )}

                  {/* Hover description for unselected styles */}
                  {isHovered && !isSelected && !isDisabled && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <span className="text-white text-xs font-medium text-center px-2">
                        {style.description}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            {!isMulti && pendingIds[0] ? (() => {
              const style = IMAGE_STYLES.find((s) => s.id === pendingIds[0]);
              if (!style) return <p className="text-sm text-stone-400">No style selected</p>;
                  return (
                    <>
                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-stone-200 shrink-0">
                        {renderPreview(style, 'w-full h-full object-cover object-top')}
                      </div>
                      <div>
                    <p className="text-sm font-bold text-stone-900">{style.label}</p>
                    <p className="text-xs text-stone-400">{style.description}</p>
                  </div>
                </>
              );
            })() : isMulti ? (
              <p className="text-sm text-stone-500">
                {pendingIds.length === 0
                  ? 'No styles picked — AI will auto-mix all images'
                  : `${pendingIds.length} style${pendingIds.length !== 1 ? 's' : ''} selected · remaining slots use AI auto-mix`}
              </p>
            ) : (
              <p className="text-sm text-stone-400">No style selected</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-stone-600 border border-stone-200 hover:border-stone-300 hover:bg-stone-50 transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-5 py-2 rounded-xl text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap"
            >
              {isMulti && pendingIds.length > 0
                ? `Apply ${pendingIds.length} Style${pendingIds.length !== 1 ? 's' : ''}`
                : 'Apply Style'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
